import load from '#core/load';


const hero_$ = '.spotlight-hero';
const video_$ = '.spotlight-hero__media--video .media-figure';
const playing_attr = 'data-playing';
const cycle_$ = '.spotlight-hero__title-highlight[data-cycle]';
const item_$ = '.spotlight-hero__title-highlight__item';
const item_c = 'spotlight-hero__title-highlight__item';
const label_c = `${item_c}-label`;
const letters_c = `${item_c}-letters`;
const letter_c = `${item_c}-letter`;
const current_c = 'is-current';
const pending_c = 'is-pending';
const untyped_c = 'is-untyped';
const caret_c = 'has-caret';
const caret_start_c = 'has-caret-start';
const hidden_attr = 'aria-hidden';
const true_s = 'true';
const reduced_motion_q = '(prefers-reduced-motion: reduce)';

// Timing of the type-and-delete cycle, in milliseconds.

// How long the caret blinks on the empty line after the page loads, before
// the first statement starts to type.
const start_time = 1500;

// The pause between one letter and the next as a statement types out.
const type_letter_time = 40;

// The longest a whole statement may take to type out. A long statement types
// its letters faster than type_letter_time so that it stays within this.
const type_max_time = 1000;

// How long a statement stays fully typed before it deletes.
const hold_time = 2500;

// The pause between one letter and the next as a statement deletes, and the
// longest a whole statement may take to delete.
const delete_letter_time = 25;
const delete_max_time = 300;

// How long the line stays empty before the next statement starts to type.
const empty_time = 250;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('spotlight-hero')) return false;

		load.content(() => {
			// The statements replace each other on a timer, so a reader who asks
			// for less motion keeps the first statement and nothing moves.
			if (window.matchMedia(reduced_motion_q).matches) return false;

			document.querySelectorAll(cycle_$).forEach($cycle => {
				start_cycle($cycle);
			});
		});
	},

};

export default module;
module.init();


/**
 * Type out and delete each statement in turn
 * @param {element} $cycle
 * @return {boolean}
 */
function start_cycle($cycle) {
	const $items = $cycle.querySelectorAll(item_$);
	if ($items.length < 2) return false;

	const statements = Array.from($items, split_statement);
	let index = 0;
	let paused = false;

	// Every step of the cycle waits through this one timer, so a pause can
	// stop the cycle wherever it is.
	let timer = null;
	const wait = (callback, time) => {
		timer = setTimeout(callback, time);
	};

	// Show a statement with no letters typed, so only the caret blinks, and
	// type it out after the wait.
	const type_after = (statement, time) => {
		show_statement(statement.$item);
		set_typed(statement.letters, 0);

		wait(() => {
			type_statement(statement, wait, () => wait(next, hold_time));
		}, time);
	};

	const next = () => {
		const statement = statements[index];

		delete_statement(statement, wait, () => {
			set_typed(statement.letters, 0, false);
			hide_statement(statement.$item);
			index = (index + 1) % statements.length;
			type_after(statements[index], empty_time);
		});
	};

	// A pause shows the current statement in full, with no caret. Playing
	// again deletes it and carries on to the next one.
	const pause = () => {
		if (paused) return;
		paused = true;
		clearTimeout(timer);

		const { letters } = statements[index];
		set_typed(letters, letters.length, false);
	};

	const resume = () => {
		if (!paused) return;
		paused = false;
		next();
	};

	watch_video($cycle, pause, resume);

	type_after(statements[index], start_time);
	$cycle.classList.remove(pending_c);

	return true;
}


/**
 * Pause and resume the cycle with the hero's background video
 *
 * The video module marks its figure with data-playing for every kind of
 * video, so the cycle follows that attribute and not the player itself.
 *
 * @param {element} $cycle
 * @param {function} pause
 * @param {function} resume
 * @return {boolean}
 */
function watch_video($cycle, pause, resume) {
	const $video = $cycle.closest(hero_$)?.querySelector(video_$);
	if (!$video) return false;

	// The figure starts out as not playing before the video autoplays. Only a
	// change from playing to not playing counts as a pause.
	let was_playing = $video.getAttribute(playing_attr) === true_s;

	const observer = new MutationObserver(() => {
		const is_playing = $video.getAttribute(playing_attr) === true_s;
		if (is_playing === was_playing) return;
		was_playing = is_playing;

		if (is_playing) resume();
		else pause();
	});
	observer.observe($video, { attributeFilter: [playing_attr] });

	return true;
}


/**
 * Split a statement into one span per letter
 *
 * The letters that are not yet typed stay in the layout, only hidden, so the
 * statement wraps onto the same lines at every step and nothing reflows as it
 * types. Screen readers get the whole statement from a hidden label instead of
 * the letter spans.
 *
 * @param {element} $item
 * @return {object}
 */
function split_statement($item) {
	const text = $item.textContent.trim();

	const $label = document.createElement('span');
	$label.className = label_c;
	$label.textContent = text;

	const $letters = document.createElement('span');
	$letters.className = letters_c;
	$letters.setAttribute(hidden_attr, true_s);

	// Spaces stay plain text, so the browser still breaks lines between words
	// and never inside one.
	const letters = [];
	Array.from(text).forEach(char => {
		if (/\s/.test(char)) {
			$letters.append(char);
			return;
		}

		const $letter = document.createElement('span');
		$letter.className = letter_c;
		$letter.textContent = char;
		$letters.append($letter);
		letters.push($letter);
	});

	$item.replaceChildren($label, $letters);

	return { $item, letters };
}


/**
 * Type a statement out one letter at a time
 * @param {object} statement
 * @param {function} wait
 * @param {function} done
 */
function type_statement({ letters }, wait, done) {
	const step = letter_time(letters.length, type_letter_time, type_max_time);
	let count = 0;

	const tick = () => {
		count += 1;
		set_typed(letters, count);

		if (count < letters.length) wait(tick, step);
		else done();
	};

	tick();
}


/**
 * Delete a statement one letter at a time, from the end
 * @param {object} statement
 * @param {function} wait
 * @param {function} done
 */
function delete_statement({ letters }, wait, done) {
	const step = letter_time(letters.length, delete_letter_time, delete_max_time);
	let count = letters.length;

	const tick = () => {
		count -= 1;
		set_typed(letters, count);

		if (count > 0) wait(tick, step);
		else done();
	};

	tick();
}


/**
 * Get the pause between letters that keeps a statement within its longest time
 * @param {number} length
 * @param {number} time
 * @param {number} max_time
 * @return {number}
 */
function letter_time(length, time, max_time) {
	return Math.min(time, max_time / Math.max(length, 1));
}


/**
 * Show the first letters of a statement and hide the rest
 *
 * The caret follows the last typed letter. With no letters typed, it sits at
 * the start of the first letter instead.
 *
 * @param {array} letters
 * @param {number} count
 * @param {boolean} caret
 */
function set_typed(letters, count, caret = true) {
	letters.forEach(($letter, i) => {
		$letter.classList.toggle(untyped_c, i >= count);
		$letter.classList.toggle(caret_c, caret && i === count - 1);
		$letter.classList.toggle(caret_start_c, caret && count === 0 && i === 0);
	});
}


/**
 * Show one statement
 * @param {element} $item
 */
function show_statement($item) {
	$item.classList.add(current_c);
	$item.removeAttribute(hidden_attr);
}


/**
 * Hide one statement
 * @param {element} $item
 */
function hide_statement($item) {
	$item.classList.remove(current_c);
	$item.setAttribute(hidden_attr, true_s);
}
